<template>
    <div class="access-denied">
        <div class="access-denied-card">
            <b-icon icon="lock-outline" size="is-large" class="access-denied-icon" />
            <h3 class="title is-5">{{ $t('You do not have access to this record.') }}</h3>
            <p class="access-denied-text">
                <template v-if="commentId">
                    {{ $t('You were mentioned in a comment here, but this record is outside your access.') }}
                </template>
                <template v-else>
                    {{ $t('You were mentioned in the description here, but this record is outside your access.') }}
                </template>
            </p>
            <div class="access-denied-actions">
                <button
                    v-if="!requested"
                    class="button is-primary"
                    :class="{ 'is-loading': sending }"
                    :disabled="sending || !canRequest"
                    @click="requestAccess"
                >
                    {{ $t('Request access') }}
                </button>
                <span v-else class="access-denied-sent">
                    <b-icon icon="check-circle-outline" size="is-small" class="has-text-success" />
                    {{ $t('Access request sent') }}
                </span>
                <button class="button is-ghost" @click="emit('done')">
                    {{ $t('Back to dashboard') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useOrgStore } from '@/stores/org';
import { useToast } from '@/composables/useToast';

const props = defineProps({
    // Morph alias + id of the record the mention points at.
    subjectType: { type: String, default: '' },
    subjectId: { type: [Number, String], default: null },
    // Set only when the mention lives in a comment on that record.
    commentId: { type: [Number, String], default: null }
});

const emit = defineEmits(['done']);

const orgStore = useOrgStore();
const toast = useToast();

const sending = ref(false);
const requested = ref(false);

const canRequest = computed(() => !!props.subjectType && !!props.subjectId);

async function requestAccess() {
    if (sending.value || !canRequest.value) return;
    sending.value = true;
    try {
        await axios.post(`${orgStore.api}/mentions/request-access`, {
            subject_type: props.subjectType,
            subject_id: props.subjectId,
            comment_id: props.commentId || null
        });
        requested.value = true;
        toast.success(i18n.t('Your access request was sent.'));
    } catch (error) {
        new AxiosError(error.response).manage();
    } finally {
        sending.value = false;
    }
}
</script>

<style scoped>
.access-denied {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;
}
.access-denied-card {
    max-width: 420px;
    text-align: center;
}
.access-denied-icon {
    color: var(--bulma-grey-light);
    margin-bottom: 0.5rem;
}
.access-denied-text {
    color: var(--bulma-text-weak);
    margin: 0.5rem 0 1.25rem;
}
.access-denied-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
}
.access-denied-sent {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--bulma-text-weak);
    font-size: 0.9rem;
}
</style>
